'use client';

import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

export interface CoinBalance {
  balance: number; // b - total balance
  frozen: number; // f - frozen balance
}

interface CoinsBalanceContextValue {
  balances: Record<string, CoinBalance>;
  setCoinBalance: (coinId: string, balance: number, frozen: number) => void;
  getCoinBalance: (coinId: string) => CoinBalance | undefined;
  getAvailableBalance: (coinId: string) => number; // Returns balance - frozen
  clearBalances: () => void;
}

const CoinsBalanceContext = createContext<CoinsBalanceContextValue | undefined>(undefined);

export function CoinsBalanceProvider({ children }: { children: ReactNode }) {
  const [balances, setBalances] = useState<Record<string, CoinBalance>>({});

  const setCoinBalance = useCallback((coinId: string, balance: number, frozen: number) => {
    setBalances((prev) => ({
      ...prev,
      [coinId]: {
        balance,
        frozen,
      },
    }));
  }, []);

  const getCoinBalance = useCallback(
    (coinId: string): CoinBalance | undefined => {
      return balances[coinId];
    },
    [balances],
  );

  const getAvailableBalance = useCallback(
    (coinId: string): number => {
      const coinBalance = balances[coinId];
      if (!coinBalance) return 0;
      return coinBalance.balance - coinBalance.frozen;
    },
    [balances],
  );

  const clearBalances = useCallback(() => {
    setBalances({});
  }, []);

  return (
    <CoinsBalanceContext.Provider
      value={{
        balances,
        setCoinBalance,
        getCoinBalance,
        getAvailableBalance,
        clearBalances,
      }}
    >
      {children}
    </CoinsBalanceContext.Provider>
  );
}

export function useCoinsBalance() {
  const context = useContext(CoinsBalanceContext);
  if (!context) {
    throw new Error('useCoinsBalance must be used within CoinsBalanceProvider');
  }
  return context;
}
